async function importKey(format, keyData, algorithm, extractable, keyUsages) {
    return crypto.subtle.importKey(
        format,
        keyData,
        algorithm,
        extractable,
        keyUsages
    );
}

async function rsaDecrypt(data, key) {
    var keyObject = await importKey(
        'pkcs8',
        base64ToUint8Array(key),
        { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
        true,
        ['decrypt']
    );
    var encodedData = await crypto.subtle.decrypt(
        {
            name: 'RSA-OAEP',
            hash: { name: 'SHA-256' },
        },
        keyObject,
        base64ToUint8Array(data)
    );
    return base64ToBinary(Uint8ArrayToBase64(encodedData));
}

function stringToUint8Array(data) {
    return Uint8Array.from(data, (c) => c.charCodeAt(0));
}

function Uint8ArrayToString(data) {
    return String.fromCharCode.apply(null, data);
}

function binaryToBase64(data) {
    return btoa(data);
}

function base64ToBinary(data) {
    return atob(data);
}

function encodeData(data) {
    return new TextEncoder('utf-8').encode(data);
}

function decodeData(data) {
    return new TextDecoder('utf-8').decode(data);
}

function base64ToUint8Array(key) {
    const binaryKey = base64ToBinary(key);
    const Uint8ArrayKey = stringToUint8Array(binaryKey);
    return Uint8ArrayKey;
}

function Uint8ArrayToBase64(data) {
    const stringData = Uint8ArrayToString(new Uint8Array(data));
    const base64Data = binaryToBase64(stringData);
    return base64Data;
}

// Function to decrypt form data
export async function decryptAllFormData(encryptedFormData, privateKey) {
    const decryptedData = [];
    try {
        for (const item of encryptedFormData) {
            const { id, first_name, last_name, email, gender, tent_name } =
                item;

            decryptedData.push({
                id,
                first_name: await rsaDecrypt(first_name, privateKey),
                gender: await rsaDecrypt(gender, privateKey),
                last_name: await rsaDecrypt(last_name, privateKey),
                email: await rsaDecrypt(email, privateKey),
                tent_name:
                    tent_name !== 'N/A'
                        ? await rsaDecrypt(tent_name, privateKey)
                        : 'N/A',
            });
        }

        return decryptedData;
    } catch (error) {
        return {
            error,
            isDecrypted: false,
        };
    }
}

// Function to decrypt form data
export async function decryptSingleFormData(encryptedFormData, privateKey) {
    const decryptedData = {};
    try {
        const { id, first_name, last_name, email, gender, tent_name } =
            encryptedFormData;

        // Decrypt each field and push the decrypted object to the decryptedData array
        decryptedData.id = id;
        decryptedData.first_name = await rsaDecrypt(first_name, privateKey);
        decryptedData.gender = await rsaDecrypt(gender, privateKey);
        decryptedData.last_name = await rsaDecrypt(last_name, privateKey);
        decryptedData.email = await rsaDecrypt(email, privateKey);
        decryptedData.tent_name = await rsaDecrypt(tent_name, privateKey);

        return decryptedData;
    } catch (error) {
        return {
            error,
            isDecrypted: false,
        };
    }
}

export async function decryptFormData(encryptedFormData, privateKey) {
    try {
        const decryptValue = async (value) => {
            if (Array.isArray(value)) {
                return await Promise.all(value.map(decryptValue));
            } else if (typeof value === 'object' && value !== null) {
                const decryptedObject = {};
                for (const [k, v] of Object.entries(value)) {
                    decryptedObject[k] = await decryptValue(v);
                }
                return decryptedObject;
            } else {
                return await rsaDecrypt(value, privateKey);
            }
        };

        const decrypted = {};
        for (const [key, value] of Object.entries(encryptedFormData)) {
            decrypted[key] = await decryptValue(value);
        }

        return decrypted;
    } catch (error) {
        return {
            error,
            isDecrypted: false,
        };
    }
}

export async function decryptTentData(encryptedFormData, privateKey) {
    const decryptedData = [];
    try {
        for (const key in encryptedFormData) {
            const {
                id,
                name,
                capacity,
                created_at,
                type,
                updated_at,
                staffs = [],
                participants = [],
            } = encryptedFormData[key];

            const decryptedTent = {
                id: id,
                name: await rsaDecrypt(name, privateKey),
                capacity: await rsaDecrypt(capacity, privateKey),
                type: await rsaDecrypt(type, privateKey),
                created_at: created_at,
                updated_at: updated_at,
                staffs: [],
                participants: [],
            };

            for (const staff of staffs) {
                decryptedTent.staffs.push({
                    id: staff.id,
                    first_name: await rsaDecrypt(staff.first_name, privateKey),
                    last_name: await rsaDecrypt(staff.last_name, privateKey),
                    role: await rsaDecrypt(staff.role, privateKey),
                });
            }

            for (const participant of participants) {
                decryptedTent.participants.push({
                    id: participant.id,
                });
            }

            decryptedData.push(decryptedTent);
        }

        return decryptedData;
    } catch (error) {
        return {
            error,
            isDecrypted: false,
        };
    }
}

export async function decryptSingleTentData(encryptedData, privateKey) {
    try {
        const {
            id,
            name,
            capacity,
            created_at,
            type,
            updated_at,
            staffs = [],
            participants = [],
        } = encryptedData;

        const decryptedTent = {
            id: id,
            name: await rsaDecrypt(name, privateKey),
            capacity: await rsaDecrypt(capacity, privateKey),
            type: await rsaDecrypt(type, privateKey),
            created_at: created_at,
            updated_at: updated_at,
            staffs: [],
            participants: [],
        };

        for (const staff of staffs) {
            decryptedTent.staffs.push({
                id: staff.id,
                first_name: await rsaDecrypt(staff.first_name, privateKey),
                last_name: await rsaDecrypt(staff.last_name, privateKey),
                role: await rsaDecrypt(staff.role, privateKey),
            });
        }

        for (const participant of participants) {
            decryptedTent.participants.push({
                id: participant.id,
                first_name: await rsaDecrypt(
                    participant.first_name,
                    privateKey
                ),
                gender: await rsaDecrypt(participant.gender, privateKey),
                last_name: await rsaDecrypt(participant.last_name, privateKey),
                email: await rsaDecrypt(participant.email, privateKey),
                tent_name: await rsaDecrypt(participant.tent_name, privateKey),
            });
        }
        return decryptedTent;
    } catch (error) {
        console.error('Decryption failed:', error);
        return {
            error,
            isDecrypted: false,
        };
    }
}
